import * as React from 'react'
import {ChevronRight, MoreHorizontal} from 'lucide-react'
import {cn} from '@/lib/utils'

const Breadcrumb = React.forwardRef<
    HTMLElement,
    React.ComponentPropsWithoutRef<'nav'> & {
        separator?: React.ReactNode
        'data-testid': string | undefined
    }
>(({'data-testid': dataTestId, ...props}, ref) => (
    <nav ref={ref} aria-label="breadcrumb" data-testid={dataTestId || 'Breadcrumb'} {...props} />
))
Breadcrumb.displayName = 'Breadcrumb'

interface BreadcrumbListProps extends React.ComponentPropsWithoutRef<'ol'> {
    className?: string
    'data-testid': string | undefined
}

const BreadcrumbList = React.forwardRef<HTMLOListElement, BreadcrumbListProps>(
    ({className, 'data-testid': dataTestId, ...props}, ref) => (
        <ol
            ref={ref}
            className={cn(
                'flex flex-wrap items-center gap-1.5 break-words text-sm text-muted-foreground sm:gap-2.5',
                className
            )}
            data-testid={dataTestId || 'BreadcrumbList'}
            {...props}
        />
    )
)
BreadcrumbList.displayName = 'BreadcrumbList'

interface BreadcrumbItemProps extends React.ComponentPropsWithoutRef<'li'> {
    className?: string
    'data-testid': string | undefined
}

const BreadcrumbItem = React.forwardRef<HTMLLIElement, BreadcrumbItemProps>(
    ({className, 'data-testid': dataTestId, ...props}, ref) => (
        <li
            ref={ref}
            className={cn('inline-flex items-center gap-1.5', className)}
            data-testid={dataTestId || 'BreadcrumbItem'}
            {...props}
        />
    )
)
BreadcrumbItem.displayName = 'BreadcrumbItem'

interface BreadcrumbPageProps extends React.ComponentPropsWithoutRef<'span'> {
    className?: string
    'data-testid': string | undefined
}

const BreadcrumbPage = React.forwardRef<HTMLSpanElement, BreadcrumbPageProps>(
    ({className, 'data-testid': dataTestId, ...props}, ref) => (
        <span
            ref={ref}
            role="link"
            aria-disabled="true"
            aria-current="page"
            className={cn('font-normal text-foreground', className)}
            data-testid={dataTestId || 'BreadcrumbPage'}
            {...props}
        />
    )
)
BreadcrumbPage.displayName = 'BreadcrumbPage'

interface BreadcrumbSeparatorProps extends React.ComponentPropsWithoutRef<'li'> {
    className?: string
    'data-testid': string | undefined
}

const BreadcrumbSeparator = ({
    children,
    className,
    'data-testid': dataTestId,
    ...props
}: BreadcrumbSeparatorProps) => (
    <li
        role="presentation"
        aria-hidden="true"
        className={cn('[&>svg]:size-3.5', className)}
        {...props}
    >
        {children ?? <ChevronRight data-testid={dataTestId || 'breadcrumb-ChevronRight'} />}
    </li>
)
BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

interface BreadcrumbEllipsisProps extends React.ComponentPropsWithoutRef<'span'> {
    className?: string
    'data-testid': string | undefined
}

const BreadcrumbEllipsis = ({
    className,
    'data-testid': dataTestId,
    ...props
}: BreadcrumbEllipsisProps) => (
    <span
        role="presentation"
        aria-hidden="true"
        className={cn('flex h-9 w-9 items-center justify-center', className)}
        {...props}
    >
        <MoreHorizontal
            className="h-4 w-4"
            data-testid={dataTestId || 'breadcrumb-MoreHorizontal'}
        />
        <span className="sr-only">More</span>
    </span>
)
BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis'

export {
    Breadcrumb,
    BreadcrumbList,
    BreadcrumbItem,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbEllipsis
}
